import React from 'react';
import { Box, Container, Typography, Link as MuiLink, Divider, IconButton, useTheme } from '@mui/material';
import { Instagram as InstagramIcon, MailOutline } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { useLocalizedPath } from '../../shared/hooks/useLocalizedPath';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';
import { accentTextColor } from '../../shared/styles';
import { Logo } from './Logo';

const contentUseCase = new ContentUseCase();

/**
 * The three legal links, and why it is exactly these three.
 *
 * The Impressum is required by § 5 DDG in Germany and § 5 ECG in Austria. The
 * privacy policy has to be reachable from every page without hunting for it
 * (Art. 12/13 GDPR). And because the site asks for consent to analytics,
 * withdrawing it has to be as easy as giving it (Art. 7(3) GDPR) - the banner
 * is gone the moment a visitor has decided, so the cookie settings need a way
 * back that does not depend on it.
 *
 * The cookie policy is not here: it is one click further on, from the privacy
 * policy and from the banner, and it is the one of the four a reader almost
 * never wants.
 */
const LEGAL_ITEMS = [
  { key: 'impressum', path: '/impressum' },
  { key: 'privacy', path: '/privacy' },
  { key: 'cookieSettings', path: '/cookie-settings' },
] as const;

/**
 * A quiet footer: the wordmark, the legal minimum, and - if an editor asks for
 * them - the two contact symbols the header already carries.
 *
 * It used to repeat the whole menu and list every legal page in full-size
 * columns, which made the end of every page longer than some of the pages. The
 * navigation is in the header where it belongs; so are Instagram and the email
 * address now; and the legal links sit on one line at the smallest readable
 * size - present, reachable, and not competing with the page above them.
 */
export const Footer: React.FC = () => {
  const theme = useTheme();
  const { language, t } = useLanguage();
  const lp = useLocalizedPath();
  const { instagram } = contentUseCase.getSocialMediaContent(language);
  const { email } = contentUseCase.getOrgInfo(language);
  const brandingVisible = contentUseCase.isFooterBrandingEnabled();

  const iconSx = {
    color: 'text.secondary',
    '&:hover': { color: accentTextColor(theme), backgroundColor: 'action.hover' },
  } as const;

  const legalLinkSx = {
    color: 'text.secondary',
    textDecoration: 'none',
    fontSize: '0.8125rem',
    '&:hover': { color: accentTextColor(theme), textDecoration: 'underline' },
  } as const;

  return (
    <Box
      component="footer"
      sx={{
        mt: 'auto',
        pt: { xs: 5, md: 6 },
        pb: 3,
        backgroundColor: 'background.default',
        borderTop: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Container maxWidth="lg">
        {brandingVisible && (
          <>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 3,
                mb: 3,
              }}
            >
              <Logo variant="full" size={44} />

              {/* Symbols, each with the name it stands for as its label - the
                  symbol carries it for people who see it, the label for
                  everyone who does not. */}
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton
                  component="a"
                  href={instagram.url}
                  target="_blank"
                  rel="noopener noreferrer me"
                  aria-label={`Instagram ${instagram.handle}`}
                  sx={iconSx}
                >
                  <InstagramIcon />
                </IconButton>
                <IconButton component="a" href={`mailto:${email}`} aria-label={email} sx={iconSx}>
                  <MailOutline />
                </IconButton>
              </Box>
            </Box>

            {/* The rule separates the wordmark from the legal line. With the
                wordmark switched off there is nothing above it to separate,
                and a line drawn over the top of the footer's own border would
                be two rules with a gap between them. */}
            <Divider sx={{ mb: 2.5 }} />
          </>
        )}

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
            © {new Date().getFullYear()} M.E.-Kollektiv. {t('footer.rights')}
          </Typography>

          <Box
            component="ul"
            aria-label={t('footer.legalTitle')}
            sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexWrap: 'wrap', gap: 2 }}
          >
            {LEGAL_ITEMS.map((item) => (
              <li key={item.key}>
                <MuiLink component={Link} to={lp(item.path)} sx={legalLinkSx}>
                  {t(`footer.${item.key}`)}
                </MuiLink>
              </li>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};
